import {
    Avatar,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Toolbar,
} from "@mui/material";
import { LanguagesDropdown } from "components/LanguagesDropdown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { paperProps, useStyles } from "./AppToolbar.styles";
import { IAppToolbar } from "./AppToolbarInterfaces";
import { useContext, useState } from "react";
import Logout from "@mui/icons-material/Logout";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { removeUser, resetLocalStorage, setToken } from "utilities/storageUtilities";
import { logout, superAdminCorporateLogin } from "api/queries/loginAPI";
import ProfileModal from "./components/ProfileModal/ProfileModal.view";
import { useTranslation } from "react-i18next";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import AppMenu from "./components/AppMenu/AppMenu.view";
import { ILoginResponse } from "components/Login/LoginInterfaces";
import { LocalStorageGlobalKeys, MADERA_BACKOFFICE_PATHS, USERTYPE } from "constants/constants";
import { useNavigate } from "react-router-dom";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsModal from "./components/NotificationsModal/NotificationsModal";
import useRolesPermissions from "hooks/useRolesPermissions";

const AppToolbar = ({ onDrawerOpen, isOpenAppDrawer, toolbarLogo }: IAppToolbar) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [isOpenProfileModal, setOpenProfileModal] = useState(false);
    const [isOpenNotificationsModal, setOpenNotificationsModal] = useState(false);
    const { currentUser, isLoadingCurrentUser, rcaEnabled, refreshCorporationsAndDivisions, refreshCurrentUser } =
        useContext(CorporateContext);
    const [anchorElForUserMenu, setAnchorElForUserMenu] = useState<null | HTMLElement>(null);
    const isOpenUserMenu = Boolean(anchorElForUserMenu);

    const { CORPORATE_PERMISSIONS } = useRolesPermissions();

    const { t } = useTranslation();

    const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        refreshCorporationsAndDivisions();
        refreshCurrentUser();
        setAnchorElForUserMenu(event.currentTarget);
    };
    const handleUserMenuClose = () => {
        setAnchorElForUserMenu(null);
    };

    const handleSuperAdminReturn = async () => {
        let loginResponse = await superAdminCorporateLogin({});

        const { data } = loginResponse as ILoginResponse;

        if (data.token) {
            setToken(data.token);
            navigate(`/${MADERA_BACKOFFICE_PATHS.ROOT_PATH}/${MADERA_BACKOFFICE_PATHS.SETUP_DASHBOARD.ROOT}`);
        }
    };

    const handleLogout = () => {
        removeUser();
        logout();
        handleUserMenuClose();
        resetLocalStorage([LocalStorageGlobalKeys.LANGUAGES, LocalStorageGlobalKeys.I18_LANGUAGE_SELECTION]);
        window.location.reload();
    };

    const handleClickProfile = () => {
        setOpenProfileModal(true);
        handleUserMenuClose();
    };

    const handleClickNotification = () => {
        setOpenNotificationsModal(true);
        handleUserMenuClose();
    };

    return (
        <Toolbar>
            <IconButton
                className={classes.toolbarIconButton}
                color="inherit"
                onClick={onDrawerOpen}
                size="small"
                edge="start"
                sx={{
                    ...(isOpenAppDrawer && { display: "none" }),
                }}
            >
                <ChevronRightIcon />
            </IconButton>
            <div className={classes.appBar}>
                <div className={classes.appBarLeftContent}>{!!toolbarLogo && toolbarLogo}</div>
                <div className={classes.appBarRightContent}>
                    <Divider orientation="vertical" flexItem className={classes.divider} />
                    <LanguagesDropdown className={classes.appBarLanguageDropdown} />
                    <Divider orientation="vertical" flexItem className={classes.divider} />
                    <Button
                        id="basic-button"
                        aria-controls={isOpenUserMenu ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={isOpenUserMenu ? "true" : undefined}
                        onClick={handleUserMenuClick}
                        className={classes.menuButton}
                        disableRipple
                    >
                        {isLoadingCurrentUser && <CircularProgress />}
                        {!isLoadingCurrentUser && currentUser && (
                            <>
                                <Avatar className={classes.avatar} src={currentUser.media?.media_url} />
                                {`${currentUser.first_name} ${currentUser.last_name}`}
                                <ExpandMoreIcon
                                    className={isOpenUserMenu ? classes.expandMoreIconRotated : classes.expandMoreIcon}
                                />
                            </>
                        )}
                    </Button>
                    <Menu
                        anchorEl={anchorElForUserMenu}
                        id="basic-menu"
                        open={isOpenUserMenu}
                        onClose={handleUserMenuClose}
                        onClick={handleUserMenuClose}
                        PaperProps={paperProps}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    >
                        <MenuItem onClick={handleClickProfile}>
                            <ListItemIcon>
                                <Avatar className={classes.avatar} src={currentUser?.media?.media_url} />
                            </ListItemIcon>
                            {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile")}
                        </MenuItem>
                        {CORPORATE_PERMISSIONS?.corporationPermissions?.showNotifications && (
                            <MenuItem onClick={handleClickNotification}>
                                <ListItemIcon>
                                    <NotificationsNoneIcon fontSize="small" />
                                </ListItemIcon>
                                {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.notifications")}
                            </MenuItem>
                        )}
                        <Divider />
                        {currentUser?.user_type?.user_type_id === USERTYPE.OSORNOADMINISTRATOR && (
                            <MenuItem onClick={handleSuperAdminReturn}>
                                <ListItemIcon>
                                    <KeyboardReturnIcon fontSize="small" />
                                </ListItemIcon>
                                {t("corporate_backoffice.corporate_layout.app_drawer.madera_bo")}
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon>
                                <Logout fontSize="small" />
                            </ListItemIcon>
                            {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.logout")}
                        </MenuItem>
                    </Menu>
                    {rcaEnabled && (
                        <>
                            <Divider orientation="vertical" flexItem className={classes.divider} />
                            <AppMenu />
                        </>
                    )}
                </div>
            </div>
            {isOpenProfileModal && (
                <ProfileModal open={isOpenProfileModal} user={currentUser} onClose={() => setOpenProfileModal(false)} />
            )}
            {isOpenNotificationsModal && (
                <NotificationsModal open={isOpenNotificationsModal} onClose={() => setOpenNotificationsModal(false)} />
            )}
        </Toolbar>
    );
};

export default AppToolbar;
